/**
 * PendingActionsModal - TaisaCore v1.7
 * Modal for viewing and approving/rejecting pending AI actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import './PendingActionsModal.css';

const PendingActionsModal = ({
  isOpen,
  onClose,
  pendingActions = [],
  onApprove,
  onReject,
  authFetch
}) => {
  const [selectedAction, setSelectedAction] = useState(null);
  const [actionDetails, setActionDetails] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  // Load action details when selected
  const loadActionDetails = useCallback(async (actionId) => {
    if (!authFetch) return;

    try {
      const response = await authFetch(`/api/orchestrator/pending-actions/${actionId}`);
      if (response.ok) {
        const data = await response.json();
        setActionDetails(data);
        setError(null);
      } else {
        setError('Failed to load action details');
      }
    } catch (err) {
      setError('Network error');
      console.error('Failed to load action details:', err);
    }
  }, [authFetch]);

  useEffect(() => {
    if (selectedAction?.id || selectedAction?.pending_action_id) {
      loadActionDetails(selectedAction.id || selectedAction.pending_action_id);
    } else {
      setActionDetails(null);
    }
  }, [selectedAction, loadActionDetails]);

  // Handle approve action
  const handleApprove = async (actionId) => {
    if (!authFetch) return;

    setProcessing(true);
    setError(null);

    try {
      const response = await authFetch(`/api/orchestrator/pending-actions/${actionId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ approved: true })
      });

      if (response.ok) {
        onApprove?.(actionId);
        setSelectedAction(null);
        setActionDetails(null);
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to approve action');
      }
    } catch (err) {
      setError('Network error');
      console.error('Failed to approve action:', err);
    } finally {
      setProcessing(false);
    }
  };

  // Handle reject action
  const handleReject = async (actionId) => {
    if (!authFetch) return;

    setProcessing(true);
    setError(null);

    try {
      const response = await authFetch(`/api/orchestrator/pending-actions/${actionId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approved: false,
          reason: rejectionReason || 'Rejected by user'
        })
      });

      if (response.ok) {
        onReject?.(actionId);
        setSelectedAction(null);
        setActionDetails(null);
        setRejectionReason('');
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to reject action');
      }
    } catch (err) {
      setError('Network error');
      console.error('Failed to reject action:', err);
    } finally {
      setProcessing(false);
    }
  };

  // Close handler
  const handleClose = () => {
    setSelectedAction(null);
    setActionDetails(null);
    setRejectionReason('');
    setError(null);
    onClose?.();
  };

  if (!isOpen) return null;

  return (
    <div className="pending-actions-modal-overlay" onClick={handleClose}>
      <div className="pending-actions-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <span className="modal-icon">🛡️</span>
            פעולות AI ממתינות לאישור
          </h2>
          <button className="close-btn" onClick={handleClose} title="סגור">×</button>
        </div>

        <div className="modal-content">
          {/* Actions List */}
          <div className="actions-list-panel">
            <div className="panel-header">
              <span>רשימת פעולות</span>
              <span className="count-badge">{pendingActions.length}</span>
            </div>

            {pendingActions.length === 0 ? (
              <div className="no-actions">
                <span className="check-icon">✓</span>
                <p>אין פעולות ממתינות</p>
              </div>
            ) : (
              <div className="actions-list">
                {pendingActions.map((action) => (
                  <div
                    key={action.id || action.pending_action_id}
                    className={`action-item ${
                      selectedAction?.id === action.id ||
                      selectedAction?.pending_action_id === action.pending_action_id
                        ? 'selected'
                        : ''
                    } risk-${(action.risk_level || 'medium').toLowerCase()}`}
                    onClick={() => setSelectedAction(action)}
                  >
                    <div className="action-item-header">
                      <span className="action-type">{formatActionType(action.action_type || action.intent_type)}</span>
                      <span className={`risk-badge ${(action.risk_level || 'medium').toLowerCase()}`}>
                        {formatRiskLevel(action.risk_level)}
                      </span>
                    </div>
                    <div className="action-item-meta">
                      <span className="channel">{action.channel || 'API'}</span>
                      <span className="time">{formatTime(action.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action Details Panel */}
          <div className="action-details-panel">
            {selectedAction && actionDetails ? (
              <>
                <div className="panel-header">
                  <span>פרטי פעולה</span>
                  <span className={`risk-indicator ${(actionDetails.risk_level || 'medium').toLowerCase()}`}>
                    {formatRiskLevel(actionDetails.risk_level)}
                  </span>
                </div>

                <div className="details-content">
                  {/* Risk Assessment */}
                  <div className="details-section">
                    <h4>הערכת סיכון</h4>
                    <div className="risk-meter">
                      <div
                        className="risk-fill"
                        style={{ width: `${(actionDetails.risk_score || 0.5) * 100}%` }}
                      />
                      <span className="risk-score-label">
                        {Math.round((actionDetails.risk_score || 0.5) * 100)}%
                      </span>
                    </div>
                    {actionDetails.risk_factors && (
                      <div className="risk-factors">
                        <span className="factors-label">גורמי סיכון:</span>
                        <ul>
                          {actionDetails.risk_factors.map((factor, i) => (
                            <li key={i} className="risk-factor">{factor}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Intent Data */}
                  <div className="details-section">
                    <h4>נתוני הפעולה</h4>
                    <div className="intent-data">
                      {actionDetails.intent_data && Object.entries(actionDetails.intent_data).map(([key, value]) => (
                        <div key={key} className="data-row">
                          <span className="data-key">{formatKey(key)}</span>
                          <span className="data-value">{formatValue(value)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Verification Status */}
                  {actionDetails.verification_required && (
                    <div className="details-section">
                      <h4>אימות</h4>
                      <div className={`verification-status ${actionDetails.verification_passed ? 'passed' : 'required'}`}>
                        {actionDetails.verification_passed ? (
                          <span>✓ אימות הושלם בהצלחה</span>
                        ) : (
                          <span>⚠ נדרש אימות - רמה {actionDetails.verification_level}</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Error Display */}
                  {error && (
                    <div className="error-message">
                      {error}
                    </div>
                  )}

                  {/* Rejection Reason Input */}
                  <div className="details-section">
                    <h4>סיבת דחייה (אופציונלי)</h4>
                    <textarea
                      className="rejection-reason-input"
                      placeholder="הזן סיבה לדחיית הפעולה..."
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      disabled={processing}
                    />
                  </div>

                  {/* Action Buttons */}
                  <div className="action-buttons">
                    <button
                      className="approve-btn"
                      onClick={() => handleApprove(actionDetails.id)}
                      disabled={processing}
                    >
                      {processing ? 'מעבד...' : '✓ אשר פעולה'}
                    </button>
                    <button
                      className="reject-btn"
                      onClick={() => handleReject(actionDetails.id)}
                      disabled={processing}
                    >
                      {processing ? 'מעבד...' : '✗ דחה פעולה'}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="no-selection">
                <span className="select-icon">👈</span>
                <p>בחר פעולה מהרשימה לצפייה בפרטים</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper functions
function formatActionType(actionType) {
  const translations = {
    'book_appointment': 'קביעת תור',
    'cancel_appointment': 'ביטול תור',
    'reschedule_appointment': 'שינוי תור',
    'create_patient': 'יצירת מטופל חדש',
    'update_patient': 'עדכון פרטי מטופל',
    'update_patient_medical': 'עדכון נתונים רפואיים',
    'get_patient_info': 'צפייה במידע מטופל',
    'send_message': 'שליחת הודעה',
    'update_preferences': 'עדכון העדפות',
    'general_query': 'שאילתה כללית'
  };
  return translations[actionType] || actionType || 'פעולה לא ידועה';
}

function formatRiskLevel(level) {
  const translations = {
    'critical': 'קריטי',
    'high': 'גבוה',
    'medium': 'בינוני',
    'low': 'נמוך'
  };
  return translations[level?.toLowerCase()] || level || 'בינוני';
}

function formatKey(key) {
  const translations = {
    'patient_id': 'מזהה מטופל',
    'patient_name': 'שם מטופל',
    'appointment_date': 'תאריך תור',
    'appointment_time': 'שעת תור',
    'provider_id': 'מזהה רופא',
    'provider_name': 'שם רופא',
    'reason': 'סיבה',
    'notes': 'הערות',
    'phone': 'טלפון',
    'email': 'דוא"ל'
  };
  return translations[key] || key;
}

function formatValue(value) {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'כן' : 'לא';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function formatTime(dateString) {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '';
  }
}

export default PendingActionsModal;
